import { lazy, Suspense } from 'react';
import { buildMapsSearchUrl } from '../utils/geo';
import { trackEvent } from '../utils/analytics';

const VenueMap = lazy(() => import('./VenueMap'));

const VENUE_COPY = {
  park: {
    placeType: 'parks',
    loading: 'Finding the closest park...',
    noneNearby: 'No parks found nearby. Time for a backyard cricket session?',
  },
  pub: {
    placeType: 'pubs',
    loading: 'Finding the closest pub...',
    noneNearby: 'No pubs found nearby. Fridge raid?',
  },
};

/**
 * The nearest park (day for it) or pub (not), with loading, honest-failure and none-nearby states.
 * @param {'park' | 'pub'} kind
 * @param {{ closest, loading, failed }} venue - from useDayForIt
 */
function VenueSection({ kind, venue, userLocation }) {
  const copy = VENUE_COPY[kind];
  // A lookup for the other kind (or none yet) means ours hasn't started: show loading.
  const isLoading = venue.loading || (venue.kind !== kind && !venue.failed);

  let content;
  if (isLoading) {
    content = (
      <p className={`${kind}-loading`} role="status" aria-live="polite">
        {copy.loading}
      </p>
    );
  } else if (venue.closest) {
    content = (
      <Suspense fallback={<div className="map-skeleton" aria-hidden="true" />}>
        <VenueMap kind={kind} venue={venue.closest} userLocation={userLocation} />
      </Suspense>
    );
  } else if (venue.failed && userLocation) {
    content = (
      <div className="error-message" role="status">
        <p>Couldn&apos;t load nearby {copy.placeType} right now.</p>
        <a
          href={buildMapsSearchUrl(copy.placeType, userLocation)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${kind}-directions-btn`}
          onClick={() => trackEvent('venue_lookup_fallback_click', { destination_type: kind })}
        >
          Search Google Maps →
        </a>
      </div>
    );
  } else {
    content = (
      <div className="error-message">
        <p>{copy.noneNearby}</p>
      </div>
    );
  }

  return <div className={`${kind}-section`}>{content}</div>;
}

export default VenueSection;
